/* eslint-disable no-undef */
import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import {
  addNewConfiguration,
  isDirtyState,
  pasteNewConfiguration,
  resetSate,
  TenantConfiguration,
  TenantConfigurationSettings,
  tenantConfigurationState,
} from "../../redux/feature/settings/configurations-slice";
import { Configuration } from "./Configuration";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { CommandBarButton } from "@fluentui/react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { navigate } from "../../redux/feature/general/navigation-slice";
import { CHOOSECONFIG } from "../../utils/constants/navigation";
import FooterPanel from "../layout/FooterPanel";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import NotificationManager from "../../common/notification/NotificationManager";
import { PASTE_CONFIGURATION_NOTIFICATION } from "../../utils/constants/notification";
import { getClipboardText } from "../../utils/helpers/Clipboard";

export interface ConfigurationsPaneProps {}

export const ConfigurationsPane: React.FunctionComponent<ConfigurationsPaneProps> = () => {
  const configurationState = useAppSelector(tenantConfigurationState);
  const dispatch = useAppDispatch();

  useEffect(() => {
    return () => {
      dispatch(resetSate());
    };
  }, []);

  const configInUse = (conf: TenantConfiguration) => {
    let activeConf = configurationState.selected ? configurationState.selected : undefined;
    return activeConf === conf.name;
  };

  const configIsAuthenticated = (conf: TenantConfiguration) => {
    let activeConf = configurationState.selected ? configurationState.selected : undefined;
    return (
      activeConf === conf.name &&
      configurationState.configurations.find((value) => value.name === configurationState.selected)?.authData
    );
  };

  const createConf = () => {
    dispatch(addNewConfiguration());
  };

  const pasteConf = async () => {
    let text = await getClipboardText();
    let parsedConf: TenantConfigurationSettings;
    console.log("Pasted Configuration: " + text);
    try {
      parsedConf = JSON.parse(text);
      dispatch(pasteNewConfiguration(parsedConf));
    } catch (e) {
      console.error(e);
      NotificationManager.addNotification({
        key: PASTE_CONFIGURATION_NOTIFICATION,
        message: formatMessage("cof.access.manageconfigurations.message.pasteconfigurationerror"),
        title: "Error",
        type: "error",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    }
  };

  return (
    <div style={{ padding: "0.5rem" }}>
      <div
        style={{
          fontSize: "1rem",
          fontWeight: "500",
          lineHeight: "1.25rem",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          color: "var(--color-blue)",
        }}
      >
        {formatMessage("cof.access.manageconfigurations")}
      </div>
      <div
        style={{
          fontWeight: "500",
          padding: "0.5rem 0 0",
          fontSize: "0.875rem",
          color: "var(--color-grey)",
        }}
      >
        {formatMessage("cof.access.manageconfigurations.info")}
      </div>
      {configurationState.configurations.map((conf, index) => (
        <div className={"shadowedCard"} key={index}>
          <Configuration
            index={index}
            key={index}
            inUse={configInUse(conf)}
            authenticated={configIsAuthenticated(conf)}
          />
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "end" }}>
        <CommandBarButton
          iconProps={{ iconName: "CircleAdditionSolid" }}
          onClick={createConf}
          style={{
            textOverflow: "ellipsis",
            marginBottom: "5px",
          }}
          disabled={isDirtyState(configurationState)}
          text={formatMessage("cof.access.manageconfigurations.addconfiguration")}
        />
        <CommandBarButton
          iconProps={{ iconName: "Paste" }}
          onClick={pasteConf}
          style={{
            textOverflow: "ellipsis",
            marginBottom: "5px",
          }}
          disabled={isDirtyState(configurationState)}
          text={formatMessage("cof.access.manageconfigurations.pasteconfiguration")}
        />
      </div>
      <FooterPanel>
        <PrimaryButton
          className={ButtonVariants.white}
          text={formatMessage("cof.base.back")}
          style={{ float: "left" }}
          onClick={() => dispatch(navigate(CHOOSECONFIG))}
        />
      </FooterPanel>
    </div>
  );
};
